import type { PointerEvent as ReactPointerEvent } from 'react';

/**
 * Shared pointer-drag lifecycle for the canvas's resize/move handles.
 *
 * Every handle followed the same dance: capture the pointer on the handle so
 * the drag survives the cursor crossing the preview iframe (which otherwise
 * swallows pointer events), listen for move/up/cancel, then tear the listeners
 * down and release the capture when the gesture ends. This centralises that so
 * the call sites only supply their move/end logic.
 *
 * Two things make the teardown reliable, and both are deliberate:
 *
 * - **Listeners live on `window`, not the handle.** A captured pointer's events
 *   are dispatched at the capture target and then bubble, so window sees every
 *   move — including over the iframe — and keeps seeing them if the capture is
 *   ever lost. Listening on the handle instead meant a lost capture silently
 *   ended delivery with no `pointercancel` to match, so the gesture never
 *   finished and its caller's state stuck until a page reload. That was the
 *   slide-reordering bug; reordering now runs on dnd-kit, but the resize handle
 *   still comes through here and could wedge the same way.
 * - **`onEnd` runs exactly once, whatever ends the gesture** — pointerup,
 *   pointercancel, the window losing focus, or the caller's own teardown. It
 *   receives the last event seen, so position-dependent cleanup still has a
 *   sensible value to work from.
 *
 * Callers own e.preventDefault()/e.stopPropagation() (they differ per handle)
 * and any early-return guards before invoking this. Returns a teardown function
 * for callers that need to end the gesture themselves (e.g. on unmount).
 */
/**
 * The gesture starts from React's synthetic event (that is what a handler
 * receives) and continues on native ones off `window`, so `onEnd` sees either.
 * The union is the honest signature: both carry the coordinates any cleanup
 * reads, and neither call site touches the React-only half.
 */
export type DragEvent = ReactPointerEvent<HTMLElement> | PointerEvent;

export type PointerDragHandlers = {
  onMove?: (e: PointerEvent) => void;
  onEnd?: (e: DragEvent) => void;
};

export function startPointerDrag(
  e: ReactPointerEvent<HTMLElement>,
  { onMove, onEnd }: PointerDragHandlers = {},
): () => void {
  const handle = e.currentTarget;
  const pointerId = e.pointerId;
  try {
    handle.setPointerCapture(pointerId);
  } catch {}

  let done = false;
  let last: DragEvent = e;

  const move = (ev: PointerEvent) => {
    if (ev.pointerId !== pointerId) return;
    last = ev;
    if (onMove) onMove(ev);
  };

  const finish = (ev: PointerEvent | null) => {
    if (done) return;
    done = true;
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
    window.removeEventListener('pointercancel', up);
    window.removeEventListener('blur', onBlur);
    try {
      handle.releasePointerCapture(pointerId);
    } catch {}
    if (onEnd) onEnd(ev || last);
  };

  const up = (ev: PointerEvent) => {
    if (ev.pointerId !== pointerId) return;
    finish(ev);
  };
  // An unfocused window never delivers the pointerup — settle on the last
  // position we saw rather than hanging.
  const onBlur = () => finish(null);

  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
  window.addEventListener('pointercancel', up);
  window.addEventListener('blur', onBlur);

  return () => finish(null);
}
